import { EventCalendar, EventCalendarProvider } from 'lego-dist';
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import * as RRule from 'rrule';
import {
  CalendarViewContainer,
  TitleCalendar,
  TitleContainer,
  SpinnerContainer,
} from './styles';
import { Subscription } from 'rxjs';
import { useTranslation } from 'react-i18next';
import schema from './formSchema';
import listEventTypes from './eventTypesList';
import { eventCalendarEventHandler } from './calendarTriggers';
import permissions from './permissions';
import ErrorModal from 'components/errorModal';
import ImagenError from '../../images/calendar/calendar_canceled_event.svg';
import { calendarEventTransformer, groupListItemTransformer } from './transformers';
import useCenters from 'hooks/centers/UseCenters';
import useAccount from 'hooks/useAccount';
import { KEY_CALENDAR_TOKEN } from './constants';
import { ENVIRONMENT } from 'config';
import EventBodyModal from './templates/EventBodyModal';
import EventListItem from './templates/EventListItem';
import ModalLoadingFeedback from '../../components/shared/ModalLoadingFeedback';
import i18n from 'i18n';
import { useRouter } from 'routes';
import useCurrentUser from 'hooks/useCurrentUser';

window.RRule = RRule;

declare global {
  interface Window {
    RRule: any;
  }
}

const config = {
  schema,
  listEventTypes,
  permissions,
  transformers: {
    calendarEventTransformer,
    groupListItemTransformer,
  },
  showWeeks: false,
  eventsDisabled: true,
  templates: {
    eventDetailsBody:(event:any, participantHooks: any) => <EventBodyModal event={event} participantHooks={participantHooks} />,
    eventCardItem: (event:any) => <EventListItem event={event} />,
  },
  responsive: {
    custom: {
      breakpoint: 600,
      height: 750,
      view: {
        calendar: {
          type: 'month',
          labels: true,
        },
      },
    },
  },
  view: {
    calendar: {
      type: 'month',
      labels: false,
    },
    agenda: { type: 'day', scrollable: true },
  },
};

const triggerEvent = (event: any) => eventCalendarEventHandler(event);

let sub: Subscription;
const OWNER_ROLE = 'Owner';

const Calendar = () => {
  const [selectedView, setSelectedView] = useState<string>('calendar');
  const [accessToken] = useState<string>(localStorage.getItem(KEY_CALENDAR_TOKEN) || '');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const router = useRouter();
  const [textError, setTextError] = useState("");
  const [t] = useTranslation();
  const { centerList } = useCenters();
  const { user } = useAccount();
  const [centersList, setCentersList] = useState<any[]>([]);


  useMemo(() => {
    if (centerList?.length > 0) {
      const mappedCenters = (centerList as any[]).map((center: any) => ({
        id: center.calendar_group_id,
        name: center.name,
        type: center.type?.toLowerCase(),
      }));
      setCentersList(mappedCenters);
    }
  }, [centerList]);

  useEffect(() => {
    eventCalendarEventHandler({
      type: 'CALENDAR_VIEW',
    });
    return () => sub?.unsubscribe();
  }, []);

  const onInit = ({ LegoEventServiceProvider }: any) => {
    sub = LegoEventServiceProvider.$listen.subscribe((event: any) => {
      console.log('### Event registered: ', event?.type);
      if (event?.type) {
        triggerEvent(event);
      }
    });
  }

  const renewAccessToken = useCallback(() => {
    console.log('Ask for a new Token to KineduAPI');
    // 1. Ask for a new token to KineduAPI
    // 2. setAccessToken(api.response.token);
  }, []);

  const RenderCalendar = useMemo(() => {
    return user?.id ? (
      <EventCalendar
        // For testing purposes switch variable to false
        live={ENVIRONMENT === 'PRODUCTION'}
        isOwner={user?.type === OWNER_ROLE}
        mainGroups={centersList}
        config={config}
        onAuthenticationFailed={renewAccessToken}
        token={accessToken}
        onViewChange={setSelectedView}
        onInit={onInit}
        dependencies={{
          rrule: RRule,
          i18n,
          useTranslation,
          router,
          hooks: {
            useCurrentUser
          },
          educatorsUser: user
        }}
        showModalError={(errorResponse: any) => {
          setTextError(errorResponse);
          setShowErrorModal(!showErrorModal);
        }}
        customInitSpinner={<ModalLoadingFeedback isLoading={true} maxWaitingSeconds={2} />}
      />
    ) : (
      <SpinnerContainer>
        <ModalLoadingFeedback isLoading={true} maxWaitingSeconds={0} />
      </SpinnerContainer>
    );
  // eslint-disable-next-line
  }, [user, accessToken, centersList]);

  return (
    <EventCalendarProvider
        constants={{ myvalue: 'hello world', name: 'Kinedu Classrooms' }}
        dependencies={{
          rrule: RRule,
          i18n,
          useTranslation,
          router,
        }}>
      <CalendarViewContainer className="calendar-view" noPadding={selectedView === 'new-form'}>
        <TitleContainer>
          <TitleCalendar>{t('calendar_events:TITLE')}</TitleCalendar>
        </TitleContainer>
        {RenderCalendar}
        {showErrorModal && (
          <ErrorModal
            title="Error"
            errorMessage={t(textError)}
            icon={ImagenError}
            showCloseButton
            onCloseModal={() => {
              setShowErrorModal(!showErrorModal);
            }}
          />
        )}
      </CalendarViewContainer>
    </EventCalendarProvider>
  );
};

export default Calendar;
